import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchSnapshot, fetchSummary, fetchHistory, fetchVIX, fetchBias, fetchWatchlist, addWatchlist, removeWatchlist } from "@/lib/api";
import TopBar from "@/components/cot/TopBar";
import CommandBar from "@/components/cot/CommandBar";
import SummaryCards from "@/components/cot/SummaryCards";
import COTTable from "@/components/cot/COTTable";
import COTChart from "@/components/cot/COTChart";
import EU10YWidget from "@/components/cot/EU10YWidget";
import USRealYieldsCard from "@/components/cot/USRealYieldsCard";
import VIXPanel from "@/components/cot/VIXPanel";
import InflationStrip from "@/components/cot/InflationStrip";
import TopHeadlinesCard from "@/components/cot/TopHeadlinesCard";
import MacroDesk from "@/components/cot/MacroDesk";
import YieldComparison from "@/components/cot/YieldComparison";
import CotExtremes from "@/components/cot/CotExtremes";
import CrowdingRegime from "@/components/cot/CrowdingRegime";
import RateDifferentials from "@/components/cot/RateDifferentials";
import AlertsEngine from "@/components/cot/AlertsEngine";
import CotAiRead from "@/components/cot/CotAiRead";
import CarryRanking from "@/components/cot/CarryRanking";
import CrossAssetMatrix from "@/components/cot/CrossAssetMatrix";
import MacroCommandBar from "@/components/cot/MacroCommandBar";
import RegimeBar from "@/components/cot/RegimeBar";
import InflationExpectationsCard from "@/components/cot/InflationExpectationsCard";
import ISMPmiCard from "@/components/cot/ISMPmiCard";
import NFPCard from "@/components/cot/NFPCard";
import CalendarCard from "@/components/cot/CalendarCard";
import WatchlistCard from "@/components/cot/WatchlistCard";
import DxyPriceTicker from "@/components/cot/DxyPriceTicker";
import CommoditiesCrossRead from "@/components/cot/CommoditiesCrossRead";
import OilRiskRead from "@/components/cot/OilRiskRead";
import { riskOffAlignment } from "@/lib/risk";
import { GitCompare } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["ALL", "FX", "Yields", "Volatility", "Commodities"];

export default function Dashboard() {
  const [sp, setSp] = useSearchParams();
  const [query, setQuery] = useState(sp.get("q") || "");
  const [category, setCategory] = useState(sp.get("cat") || "ALL");
  const [sortKey, setSortKey] = useState(sp.get("sk") || "open_interest");
  const [sortDir, setSortDir] = useState(sp.get("sd") || "desc");
  const [selectedCode, setSelectedCode] = useState(sp.get("m") || null);
  const [weeks, setWeeks] = useState(parseInt(sp.get("w") || "52", 10));
  const [riskOffEnabled, setRiskOffEnabled] = useState(sp.get("ro") === "1");
  const [riskOffThreshold, setRiskOffThreshold] = useState(parseInt(sp.get("rt") || "20", 10));

  useEffect(() => {
    const next = new URLSearchParams();
    if (query) next.set("q", query);
    if (category !== "ALL") next.set("cat", category);
    if (sortKey !== "open_interest") next.set("sk", sortKey);
    if (sortDir !== "desc") next.set("sd", sortDir);
    if (selectedCode) next.set("m", selectedCode);
    if (weeks !== 52) next.set("w", String(weeks));
    if (riskOffEnabled) next.set("ro", "1");
    if (riskOffThreshold !== 20) next.set("rt", String(riskOffThreshold));
    setSp(next, { replace: true });
  }, [query, category, sortKey, sortDir, selectedCode, weeks, riskOffEnabled, riskOffThreshold, setSp]);

  const snapshotQ = useQuery({
    queryKey: ["snapshot"],
    queryFn: fetchSnapshot,
    refetchInterval: 1000 * 60 * 10,
  });

  const summaryQ = useQuery({ queryKey: ["summary"], queryFn: fetchSummary });

  const historyQ = useQuery({
    queryKey: ["history", selectedCode, weeks],
    queryFn: () => fetchHistory(selectedCode, weeks),
    enabled: !!selectedCode,
  });

  const vixQ = useQuery({
    queryKey: ["vix", 180],
    queryFn: () => fetchVIX(180),
    staleTime: 1000 * 60 * 15,
  });
  const vixLevel = vixQ.data?.latest?.vix ?? null;
  const riskOffActive =
    riskOffEnabled && vixLevel != null && vixLevel >= riskOffThreshold;

  const usdBiasQ = useQuery({
    queryKey: ["bias", "USD"],
    queryFn: () => fetchBias("USD"),
    staleTime: 1000 * 60 * 15,
  });
  const usdTint = {
    active: (usdBiasQ.data?.confidence ?? 0) >= 70 && usdBiasQ.data?.bias !== "neutral",
    bias: usdBiasQ.data?.bias || "neutral",
    confidence: usdBiasQ.data?.confidence ?? 0,
  };

  const qc = useQueryClient();
  const watchlistQ = useQuery({ queryKey: ["watchlist"], queryFn: fetchWatchlist, staleTime: 1000 * 60 });
  const watchItems = useMemo(() => watchlistQ.data?.items ?? [], [watchlistQ.data?.items]);
  const watchedCodes = useMemo(() => new Set(watchItems.map((i) => i.code)), [watchItems]);

  const toggleWatch = async (m) => {
    const isWatched = watchedCodes.has(m.code);
    try {
      if (isWatched) {
        await removeWatchlist(m.code);
        toast.success(`Unpinned ${m.symbol}`);
      } else {
        await addWatchlist({ code: m.code, symbol: m.symbol, name: m.name });
        toast.success(`Pinned ${m.symbol} to watchlist`);
      }
      qc.invalidateQueries({ queryKey: ["watchlist"] });
    } catch {
      toast.error("Could not update watchlist");
    }
  };

  const removeWatch = async (code) => {
    try {
      await removeWatchlist(code);
      qc.invalidateQueries({ queryKey: ["watchlist"] });
    } catch {
      toast.error("Could not remove from watchlist");
    }
  };

  // Auto-select first market on first load
  useEffect(() => {
    if (!selectedCode && snapshotQ.data?.markets?.length) {
      setSelectedCode(snapshotQ.data.markets[0].code);
    }
  }, [snapshotQ.data, selectedCode]);

  useEffect(() => {
    if (snapshotQ.error) toast.error("Failed to load CFTC snapshot");
    if (summaryQ.error) toast.error("Failed to load KPI summary");
  }, [snapshotQ.error, summaryQ.error]);

  const markets = useMemo(() => snapshotQ.data?.markets ?? [], [snapshotQ.data]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let rows = markets.filter((m) => {
      if (category !== "ALL" && m.category !== category) return false;
      if (!q) return true;
      return (
        m.name.toLowerCase().includes(q) ||
        m.symbol.toLowerCase().includes(q) ||
        m.category.toLowerCase().includes(q)
      );
    });
    rows = rows.map((m) => ({
      ...m,
      _alignment: riskOffActive
        ? riskOffAlignment(m.symbol, m.change_net_noncomm)
        : null,
    }));
    rows = [...rows].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (typeof av === "string") {
        return sortDir === "asc"
          ? av.localeCompare(bv)
          : bv.localeCompare(av);
      }
      return sortDir === "asc" ? av - bv : bv - av;
    });
    return rows;
  }, [markets, query, category, sortKey, sortDir, riskOffActive]);

  const alignedCount = filtered.filter((r) => r._alignment === "aligned").length;

  const onSort = (key) => {
    if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const selectedMarket = markets.find((m) => m.code === selectedCode);

  return (
    <div className="min-h-screen bg-black text-zinc-100" data-testid="cot-dashboard">
      <AlertsEngine
        markets={snapshotQ.data?.markets || []}
        bias={usdBiasQ.data}
        vix={vixLevel}
        threshold={riskOffThreshold}
      />
      <div className="sticky top-0 z-30 bg-black">
        <TopBar
          reportDate={snapshotQ.data?.report_date}
          loading={snapshotQ.isFetching || summaryQ.isFetching}
        />
        <DxyPriceTicker />
      </div>

      <main className="px-3 lg:px-6 py-4 space-y-4 max-w-[2000px] mx-auto">
        {/* Macro tone + risk-off, then market regime as its own bar */}
        <MacroCommandBar
          vix={vixLevel}
          threshold={riskOffThreshold}
          riskOffEnabled={riskOffEnabled}
          onToggle={setRiskOffEnabled}
          onThreshold={setRiskOffThreshold}
          alignedCount={alignedCount}
        />
        <RegimeBar
          markets={snapshotQ.data?.markets || []}
          vix={vixLevel}
          threshold={riskOffThreshold}
        />

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 items-start">
          {/* ══ MAIN COLUMN (75%) ══ */}
          <div className="lg:col-span-3 space-y-4 min-w-0" data-testid="main-column">
            <ZoneHeader n="01" label="Macro Desk" hint="AI narrative · bias · real yields" />
            <MacroDesk />
            <YieldComparison />
            <RateDifferentials />
            <USRealYieldsCard />

            <ZoneHeader n="02" label="CFTC Positioning" hint="Commitment of Traders · non-commercials · Legacy futures" />
            <CrowdingRegime rows={snapshotQ.data?.markets || []} />
            <CotExtremes rows={snapshotQ.data?.markets || []} onSelect={setSelectedCode} />
            <CotAiRead />
            <CommandBar
              query={query}
              onQuery={setQuery}
              category={category}
              onCategory={setCategory}
              categories={CATEGORIES}
              weeks={weeks}
              onWeeks={setWeeks}
              marketsCount={filtered.length}
              totalCount={markets.length}
            />
            <COTTable
              rows={filtered}
              sortKey={sortKey}
              sortDir={sortDir}
              onSort={onSort}
              selectedCode={selectedCode}
              onSelect={setSelectedCode}
              loading={snapshotQ.isLoading}
              usdTint={usdTint}
              watched={watchedCodes}
              onToggleWatch={toggleWatch}
            />

            {/* Selected card · net positioning chart */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-stretch">
              <MarketSummaryPanel market={selectedMarket} />
              <COTChart
                market={selectedMarket}
                history={historyQ.data}
                loading={historyQ.isLoading}
              />
            </div>

            {/* Quick-glance KPI cards */}
            <SummaryCards summary={summaryQ.data} loading={summaryQ.isLoading} />

            {/* Cross-asset correlation + carry ranking, side by side */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start" data-testid="main-correlation-carry">
              <CrossAssetMatrix />
              <CarryRanking />
            </div>
          </div>

          {/* ══ SIDEBAR (25%) ══ */}
          <aside className="lg:col-span-1 space-y-4 min-w-0" data-testid="dashboard-sidebar">
            <ZoneHeader n="03" label="Watch Sidebar" hint="cross-reads · movers · watchlist · vol" />
            <CommoditiesCrossRead />
            <OilRiskRead />
            <TopHeadlinesCard />
            <WatchlistCard
              items={watchItems}
              markets={markets}
              onSelect={setSelectedCode}
              onRemove={removeWatch}
            />
            <InflationStrip variant="compact" />
            <InflationExpectationsCard />
            <NFPCard />
            <ISMPmiCard />
            <CalendarCard />
            <VIXPanel />
            <EU10YWidget />
          </aside>
        </div>

        {/* Full-width footer control · risk-off switch */}
        <footer className="text-[11px] uppercase tracking-widest text-zinc-600 pt-2 pb-6 border-t border-zinc-900/80 flex flex-wrap items-center gap-x-3 gap-y-2">
          <span>
            <span className="text-zinc-500">Source:</span> CFTC Public Reporting / Legacy Futures Only ·
            <span className="ml-1">FRED · ECB · Eurostat · Yahoo Finance · Data cached server-side · Released weekly (Fridays)</span>
          </span>
          <Link
            to="/compare"
            data-testid="open-compare"
            className="ml-auto inline-flex items-center gap-2 border border-zinc-700 px-2 py-1.5 text-[11px] uppercase tracking-widest text-zinc-300 transition-colors hover:border-amber-500 hover:text-amber-term"
          >
            <GitCompare size={12} strokeWidth={1.5} /> Open Pair-Comparison
          </Link>
        </footer>
      </main>
    </div>
  );
}

function ZoneHeader({ n, label, hint }) {
  return (
    <div className="flex items-baseline gap-3 pt-1" data-testid={`zone-header-${n}`}>
      <span className="text-[11px] font-mono text-amber-term/60 tabular-nums">{n}</span>
      <h2 className="text-[11px] uppercase tracking-[0.28em] text-zinc-200 font-mono whitespace-nowrap">
        {label}
      </h2>
      {hint && (
        <span className="hidden sm:inline text-[11px] uppercase tracking-widest text-zinc-600 truncate">
          {hint}
        </span>
      )}
      <span className="flex-1 border-b border-zinc-800/70 ml-1 translate-y-[-3px]" />
    </div>
  );
}

function MarketSummaryPanel({ market }) {
  if (!market) {
    return (
      <div
        className="border border-zinc-800 bg-[#09090B] p-3 h-full min-h-[280px] flex items-center justify-center text-zinc-600 text-xs uppercase tracking-widest"
        data-testid="market-summary-empty"
      >
        select a market
      </div>
    );
  }
  const fmt = (n) => new Intl.NumberFormat("en-US").format(n);
  const sign = (n) => (n > 0 ? "text-pos" : n < 0 ? "text-neg" : "text-zinc-400");
  const signed = (n) => `${n > 0 ? "+" : n < 0 ? "-" : ""}${fmt(Math.abs(n))}`;
  return (
    <div
      className="border border-zinc-800 bg-[#09090B] p-3 h-full"
      data-testid="market-summary-panel"
    >
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-zinc-500">SELECTED</div>
          <div className="text-base font-semibold text-amber-term">{market.symbol} — {market.name}</div>
        </div>
        <div className="text-[11px] uppercase tracking-widest text-zinc-500">
          {market.category}
        </div>
      </div>
      <SummaryRow label="Report Date" value={market.report_date} />
      <SummaryRow label="Open Interest" value={fmt(market.open_interest)} />
      <SummaryRow label="Δ OI (WoW)" value={signed(market.change_open_interest)} cls={sign(market.change_open_interest)} />
      <SummaryRow label="Non-Comm Long" value={fmt(market.noncomm_long)} cls="text-pos" />
      <SummaryRow label="Non-Comm Short" value={fmt(market.noncomm_short)} cls="text-neg" />
      <SummaryRow label="Net Non-Comm" value={signed(market.net_noncomm)} cls={sign(market.net_noncomm)} />
      <SummaryRow label="Commercial Long" value={fmt(market.comm_long)} cls="text-pos" />
      <SummaryRow label="Commercial Short" value={fmt(market.comm_short)} cls="text-neg" />
      <SummaryRow label="Net Commercial" value={signed(market.net_comm)} cls={sign(market.net_comm)} />
      <SummaryRow label="Δ Net Non-Comm" value={signed(market.change_net_noncomm)} cls={sign(market.change_net_noncomm)} />
      <SummaryRow label="Δ Net Commercial" value={signed(market.change_net_comm)} cls={sign(market.change_net_comm)} />
    </div>
  );
}

function SummaryRow({ label, value, cls = "" }) {
  return (
    <div className="flex items-center justify-between border-b border-zinc-900 py-1.5">
      <span className="text-[11px] uppercase tracking-widest text-zinc-500">{label}</span>
      <span className={`text-xs tabular-nums ${cls}`}>{value}</span>
    </div>
  );
}
